import React, { useState } from 'react';
import { sendFeedback } from '../services/feedback';
import './Feedback.css';

/**
 * Feedback form.
 *
 * The email field is optional and only asked for when we do not already know
 * who this is: someone reporting that their chart is wrong should not have to
 * make an account before they can tell us.
 */
function Feedback({ onClose, account, profileName }) {
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) {
      setError('Please write something first.');
      return;
    }

    setStatus('sending');
    setError('');
    try {
      await sendFeedback({ message: trimmed, email: email.trim(), profile: profileName });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError('That did not send. Check your connection and try again.');
    }
  };

  return (
    <div
      className="feedback-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
    >
      <div className="feedback-backdrop" onClick={onClose} />
      <div className="feedback-panel">
        <button
          type="button"
          className="feedback-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {status === 'sent' ? (
          <>
            <span className="feedback-glyph" aria-hidden="true">✓</span>
            <h3 id="feedback-title">Thank you</h3>
            <p className="feedback-sub">
              Your message is with us. If you left an address and it needs a
              reply, you will hear back.
            </p>
            <button type="button" className="feedback-cta" onClick={onClose}>
              Close
            </button>
          </>
        ) : (
          <>
            <span className="feedback-glyph" aria-hidden="true">✦</span>
            <h3 id="feedback-title">Send feedback</h3>
            <p className="feedback-sub">
              Found something wrong in a reading, or something you would like
              Astrobot to do? Tell us plainly — it goes straight to the people
              building it.
            </p>

            <form onSubmit={submit}>
              <textarea
                className="feedback-input"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (error) setError('');
                }}
                placeholder="What happened, or what would you like to see?"
                rows={5}
                maxLength={5000}
                autoFocus
                disabled={status === 'sending'}
              />

              {!account && (
                <input
                  type="email"
                  className="feedback-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email (optional, so we can reply)"
                  autoComplete="email"
                  disabled={status === 'sending'}
                />
              )}

              {error && <div className="feedback-error" role="alert">{error}</div>}

              <button
                type="submit"
                className="feedback-cta"
                disabled={status === 'sending' || !message.trim()}
              >
                {status === 'sending' ? 'Sending…' : 'Send feedback'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Feedback;
